import React, {
	useMemo,
	useRef,
	useCallback,
	useEffect,
	useState,
} from "react";
import useMeasure from "react-use-measure";
import { mergeRefs, useDrag, useTh } from "leva/plugin";
import { Svg } from "../styles/StyledBezier";
import type {
	Bezier as BezierType,
	BezierProps,
	DisplayValueBezier,
} from "../types/bezier-types";
import { getDistanceBetweenPoints2D } from "../../jsx/aeft/math-utils";
import { displayGridIncrement } from "../assets/constants";
import { CubicBezNotationModal } from "../components/CubicBezNotationModal";
import { ContextMenu } from "../components/ContextMenu";
import { clearKeyframeEasingRequest } from "../utilities/layers";
import { useGlobalState } from "../hooks/useGlobalState";
import { ResizeObserver } from "@juggle/resize-observer";

const HANDLE_RADIUS = 4;
interface LineProps {
	sx: number;
	sy: number;
	cx: number;
	cy: number;
}

const useRange = () => {
	return useCallback((v: number, size) => size * v, []);
};

const useInvertedRange = () => {
	return useCallback((v: number, size) => v / size, []);
};

function Line({ sx, sy, cx, cy }: LineProps) {
	const a = Math.atan2(cy - sy, cx - sx);
	const cxs = cx - HANDLE_RADIUS * Math.cos(a);
	const cys = cy - HANDLE_RADIUS * Math.sin(a);

	return <line x1={cxs} y1={cys} x2={sx} y2={sy} />;
}

export function BezierSvg({
	displayValue,
	onUpdate,
	withPreview,
	resetPreviewDuration,
	clearKeyframeEasing,
	isInverted,
	presetBezierBarFull,
}: Pick<BezierProps, "displayValue" | "onUpdate"> & {} & {
	withPreview: boolean;
} & { isInverted: boolean } & {
	resetPreviewDuration: Function;
} & { clearKeyframeEasing: Function } & { presetBezierBarFull: boolean }) {
	const r = useRange();
	const ir = useInvertedRange();
	const [ref, { width, height }] = useMeasure({ polyfill: ResizeObserver });
	const svgRef = useRef<SVGSVGElement>(null);
	const handleLeft = useRef<SVGCircleElement>(null);
	const handleRight = useRef<SVGCircleElement>(null);
	const bounds = useRef<DOMRect>();
	const { keysHeld } = useGlobalState();
	const keysHeldRef = useRef({
		shift: false,
		ctrl: false,
	});

	const [dragging, setDragging] = useState(false);
	const [index, setIndex] = useState(0);
	const accentColor = useTh("colors", "highlight3");
	const [cubicBezNotationModalOpen, setCubicBezNotationModalOpen] =
		useState(false);
	const [rightClickMousePosition, setRightClickMousePosition] = useState({
		x: 0,
		y: 0,
	});
	const [contextMenuOpen, setContextMenuOpen] = useState(false);

	useEffect(() => {
		keysHeldRef.current.shift = keysHeld.shift;
		keysHeldRef.current.ctrl = keysHeld.ctrl;
	}, [keysHeld]);

	const handleMouseDown = (e: { clientX: any; clientY: any }) => {
		//@ts-ignore
		if (e.button === 2) return;
		if (!dragging) {
			let x = e.clientX;
			let y = e.clientY;
			let memo = 0;
			bounds.current = svgRef.current!.getBoundingClientRect();
			let relX = x - bounds.current!.left;
			let relY = y - bounds.current!.top;
			let distToRight = getDistanceBetweenPoints2D(relX, relY, cx2, cy2);
			let distToLeft = getDistanceBetweenPoints2D(relX, relY, cx1, cy1);
			if (distToRight < distToLeft) {
				memo = 2;
			}
			if (isInverted) {
				relY = height - relY;
				relX = width - relX;
			}
			onUpdate((v: BezierType) => {
				const newV = [...v];
				isInverted
					? (newV[memo] = 1 - ir(relX, width))
					: (newV[memo] = ir(relX, width));
				newV[memo + 1] = 1 - ir(relY, height);
				return newV;
			});
			setIndex(memo);
			setDragging(true);
			resetPreviewDuration();
		}
	};

	const handleMouseMove = (e: { clientX: any; clientY: any }) => {
		bounds.current = svgRef.current!.getBoundingClientRect();
		if (dragging) {
			let x = e.clientX;
			let y = e.clientY;
			bounds.current = svgRef.current!.getBoundingClientRect();
			let relX = x - bounds.current!.left;
			let relY = y - bounds.current!.top;

			if (keysHeldRef.current.shift) {
				let numYLines = Math.floor(height / displayGridIncrement);
				let yRemainder = height % displayGridIncrement;
				let yIncrementStretch = yRemainder ? yRemainder / numYLines : 0;
				let multiple = Math.floor(relY / displayGridIncrement);
				relY = multiple * displayGridIncrement + yIncrementStretch * multiple;
			}
			if (keysHeldRef.current.ctrl) {
				let numXLines = Math.floor(width / displayGridIncrement);
				let xRemainder = width % displayGridIncrement;
				let xIncrementStretch = xRemainder ? xRemainder / numXLines : 0;
				let multiple = Math.floor(relX / displayGridIncrement);
				relX = multiple * displayGridIncrement + xIncrementStretch * multiple;
			}

			if (isInverted) {
				relX = width - relX;
			}

			onUpdate((v: BezierType) => {
				const newV = [...v];
				isInverted
					? (newV[index] = 1 - ir(relX, width))
					: (newV[index] = ir(relX, width));
				isInverted
					? (newV[index + 1] = ir(relY, height))
					: (newV[index + 1] = 1 - ir(relY, height));
				return newV;
			});
		}
	};

	const handleMouseUp = () => {
		setDragging(false);
	};

	useEffect(() => {
		if (dragging) {
			// Add the window event listeners when dragging starts
			window.addEventListener("mousemove", handleMouseMove);
			window.addEventListener("mouseup", handleMouseUp);
		} else {
			// Remove the window event listeners when dragging stops
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		}

		// Cleanup function
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragging]); // Add dragging as a dependency

	let { x1, y1, x2, y2 } = displayValue;

	const { sx, sy, ex, ey, cx1, cy1, cx2, cy2 } = useMemo(
		() => ({
			sx: r(0, width),
			sy: isInverted ? r(0, height) : r(1, height),
			ex: r(1, width),
			ey: isInverted ? r(1, height) : r(0, height),
			cx1: r(x1, width),
			cy1: isInverted ? r(y1, height) : r(1 - y1, height),
			cx2: r(x2, width),
			cy2: isInverted ? r(y2, height) : r(1 - y2, height),
		}),
		[r, x1, y1, x2, y2, width, height, isInverted]
	);

	//@ts-ignore
	const gridLines = ({ width, height }) => {
		const lines = useMemo(() => {
			const final: any[] = [];
			let numXLines = Math.floor(width / displayGridIncrement);
			let xRemainder = width % displayGridIncrement;
			let xIncrementStretch = xRemainder ? xRemainder / numXLines : 0;
			let numYLines = Math.floor(height / displayGridIncrement);
			let yRemainder = height % displayGridIncrement;
			let yIncrementStretch = yRemainder ? yRemainder / numYLines : 0;
			for (let i = 0; i <= numXLines; i++) {
				let x = i * displayGridIncrement + xIncrementStretch * i;
				final.push(
					<line
						key={`x${i}`}
						x1={x}
						y1={0}
						x2={x}
						y2={height}
						style={{ stroke: accentColor, strokeWidth: ".5", opacity: ".18" }}
					/>
				);
			}
			for (let i = 0; i <= numYLines; i++) {
				let y = i * displayGridIncrement + yIncrementStretch * i;
				final.push(
					<line
						key={`y${i}`}
						x1={0}
						y1={y}
						x2={width}
						y2={y}
						style={{ stroke: accentColor, strokeWidth: ".5", opacity: ".18" }}
					/>
				);
			}

			return final;
		}, [width, height]);

		return <>{lines}</>;
	};
	let final = gridLines({ width, height });

	const handleContextMenu = (
		event: React.MouseEvent<SVGSVGElement, MouseEvent>
	) => {
		event.preventDefault();
		// setCubicBezNotationModalOpen(true);
		setRightClickMousePosition({ x: event.clientX, y: event.clientY });
		setContextMenuOpen(true);
	};

	const handleContextMenuSelection = (selection: string) => {
		if (selection === "cubic-bezier") {
			setCubicBezNotationModalOpen(true);
		} else if (selection === "clear") {
			clearKeyframeEasing();
		}
		setContextMenuOpen(false);
	};

	const handleApplyCubicBezierNotation = (cubicBezNotation: string) => {
		const [x1, y1, x2, y2] = cubicBezNotation.split(",").map(parseFloat);
		onUpdate((v: BezierType) => {
			return [x1, y1, x2, y2];
		});
		setCubicBezNotationModalOpen(false);
	};

	const roundDisplayValue = (value: DisplayValueBezier) => {
		return `${Math.round(value.x1 * 100) / 100}, ${
			Math.round(value.y1 * 100) / 100
		}, ${Math.round(value.x2 * 100) / 100}, ${
			Math.round(value.y2 * 100) / 100
		}`;
	};

	return (
		<div
			className={`transition-all duration-150 z-[2020] ${
				presetBezierBarFull ? "opacity-0" : "opacity-100"
			}`}
		>
			{cubicBezNotationModalOpen && (
				<CubicBezNotationModal
					displayValue={roundDisplayValue(displayValue)}
					handleApplyCubicBezierNotation={handleApplyCubicBezierNotation}
				/>
			)}
			{contextMenuOpen && (
				<ContextMenu
					pos={rightClickMousePosition}
					handleContextMenuSelection={handleContextMenuSelection}
				/>
			)}
			<Svg
				style={{ touchAction: "none", width: "100%", height: "100%" }}
				ref={mergeRefs([svgRef, ref])}
				withPreview={withPreview}
				onMouseDown={handleMouseDown}
				onContextMenu={handleContextMenu}
				preserveAspectRatio="xMidYMid meet"
				viewBox={"0 0 " + width + " " + height}
			>
				{final}
				<line x1={sx} y1={sy} x2={ex} y2={ey} />
				<path
					fill="none"
					d={`M${sx},${sy} C${cx1},${cy1} ${cx2},${cy2} ${ex},${ey}`}
					strokeLinecap="round"
				/>
				<g>
					<Line sx={sx} sy={sy} cx={cx1} cy={cy1} />
					<circle ref={handleLeft} cx={cx1} cy={cy1} r={HANDLE_RADIUS} />
				</g>
				<g>
					<Line sx={ex} sy={ey} cx={cx2} cy={cy2} />
					<circle ref={handleRight} cx={cx2} cy={cy2} r={HANDLE_RADIUS} />
				</g>
			</Svg>
		</div>
	);
}
